'use client';

import { useState, useEffect } from 'react';
import { signIn } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';

interface AuthFormProps {
  callbackUrl?: string;
  verified?: boolean;
  mode?: 'signin' | 'signup';
}

type AuthStep = 'email' | 'verify-email' | 'password' | 'signin';

export default function AuthForm({ callbackUrl = '/home', verified = false, mode = 'signup' }: AuthFormProps) {
  const router = useRouter();
  // Set initial step based on whether we're in signin or signup mode
  const [step, setStep] = useState<AuthStep>(mode === 'signin' ? 'signin' : 'email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState(mode === 'signin');

  // Make sure step is set correctly when mode changes
  useEffect(() => {
    if (mode === 'signin') {
      setStep('signin');
      setIsExistingUser(true);
    } else {
      setStep('email');
      setIsExistingUser(false);
    }
  }, [mode]);

  // Check if email exists when user enters email
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);

    try {
      // If we're already in signin mode, skip email check and go straight to signin form
      if (mode === 'signin') {
        setStep('signin');
        setIsLoading(false);
        return;
      }
      
      // For signup mode, check if user exists
      const response = await fetch('/api/auth/check-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();
      console.log(`Email check response for ${email}:`, data);

      if (response.ok) {
        if (data.exists) {
          console.log(`User exists, switching to signin mode`);
          setIsExistingUser(true);
          // If user exists but has no password, offer to set one
          if (!data.hasPassword) {
            setStep('password'); // Let them set a password
            toast.warning('Email already registered', { description: 'Please set a password to complete your account setup.' });
          } else {
            setStep('signin');
          }
        } else {
          console.log(`New user, switching to password/registration mode`);
          setIsExistingUser(false);
          setStep('password');
        }
      } else {
        toast.error('Something went wrong', { description: data.error });
      }
    } catch (error) {
      toast.error('Network error', { description: 'Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle user registration
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsLoading(true);

    try {
      // If this is an existing Google user setting a password
      if (isExistingUser) {
        if (!email || !password) return;
        
        const response = await fetch('/api/auth/set-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password })
        });

        const data = await response.json();

        if (response.ok) {
          setStep('signin');
          toast.success('Password set successfully!', { description: 'You can now sign in.' });
        } else {
          toast.error('Failed to set password', { description: data.error });
        }
      } else {
        // Regular new user registration
        if (!email || !password || !name) return;

        const response = await fetch('/api/auth/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password, name })
        });

        const data = await response.json();

        if (response.ok) {
          setStep('verify-email');
        } else {
          toast.error('Registration failed', { description: data.error });
        }
      }
    } catch (error) {
      toast.error('Network error', { description: 'Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle user sign in
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    setIsLoading(true);

    try {
      // Only check if user exists if we haven't already confirmed
      if (!isExistingUser) {
        // Check if user exists first
        const response = await fetch('/api/auth/check-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });

        const data = await response.json();

        if (response.ok && !data.exists) {
          toast.warning('No account found', { description: 'No account found with this email.' });
          setIsLoading(false);
          return;
        }
      }

      const result = await signIn('credentials', {
        email,
        password,
        redirect: false,
      });

      if (result?.error) {
        // Check if user needs to set up password (previously Google user)
        if (result.error === 'SETUP_PASSWORD_REQUIRED') {
          setStep('password');
          toast.info('Password setup required', { description: 'This account was created with Google Sign-in. Please set up a password to continue.' });
          setIsExistingUser(true); // Show "Set Password" form, not new registration form
        } else {
          toast.error('Sign in failed', { description: result.error });
        }
      } else if (result?.ok) {
        // Fetch updated session to check onboarding status
        const updatedSession = await fetch('/api/auth/session').then(res => res.json());
        
        // Check if user needs to complete onboarding
        if (updatedSession?.user && !updatedSession.user.onboardingComplete) {
          console.log('🔄 Redirecting to onboarding - user has not completed onboarding');
          router.push('/onboarding');
        } else {
          router.push(callbackUrl);
        }
      }
    } catch (error) {
      toast.error('Sign in failed', { description: 'Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setStep('email');
    setEmail('');
    setPassword('');
    setName('');
    setIsExistingUser(false);
  };

  return (
    <div className="max-w-md mx-auto bg-card p-8 rounded-lg shadow-md">
      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-foreground">
          {step === 'verify-email' ? 'Check Your Email' : 
           step === 'signin' ? 'Welcome Back' : 
           step === 'password' ? 'Create Account' : 'Get Started'}
        </h1>
        <p className="text-muted-foreground mt-2">
          {step === 'verify-email' ? 'We sent you a verification link' :
           step === 'signin' ? 'Sign in to your account' :
           step === 'password' ? 'Complete your registration' : 'Enter your work email to continue'}
        </p>
      </div>

      {/* Success message for verified email */}
      {verified && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
          ✅ Email verified successfully! You can now sign in.
        </div>
      )}

      {/* Email Step */}
      {step === 'email' && (
        <form onSubmit={handleEmailSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground">
              Work Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="mt-1 block w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground mt-1">
              We only accept work emails (no Gmail, Yahoo, etc.)
            </p>
          </div>
          <button
            type="submit"
            disabled={isLoading || !email}
            className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Checking...' : 'Continue'}
          </button>
        </form>
      )}

      {/* Registration Step */}
      {step === 'password' && !isExistingUser && (
        <form onSubmit={handleSignUp} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-foreground">
              Full Name
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              className="mt-1 block w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
              disabled={isLoading}
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-foreground">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              className="mt-1 block w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
              minLength={8}
              disabled={isLoading}
            />
          </div>
          <div className="text-sm text-muted-foreground">
            <p>Creating account for: <strong>{email}</strong></p>
            <button
              type="button"
              onClick={resetForm}
              className="text-primary-600 hover:underline"
            >
              Use different email
            </button>
          </div>
          <button
            type="submit"
            disabled={isLoading || !name || !password}
            className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>
      )}

      {/* Password Setup for Existing Google Users */}
      {step === 'password' && isExistingUser && (
        <form onSubmit={handleSignUp} className="space-y-4">
          <div>
            <label htmlFor="password-setup" className="block text-sm font-medium text-foreground">
              Set Password
            </label>
            <input
              id="password-setup"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              className="mt-1 block w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
              minLength={8}
              disabled={isLoading}
            />
          </div>
          <div className="text-sm text-muted-foreground">
            <p>Setting password for: <strong>{email}</strong></p>
            <button
              type="button"
              onClick={resetForm}
              className="text-primary-600 hover:underline"
            >
              Use different email
            </button>
          </div>
          <button
            type="submit"
            disabled={isLoading || !password}
            className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Setting Password...' : 'Set Password'}
          </button>
        </form>
      )}

      {/* Sign In Step */}
      {step === 'signin' && (
        <form onSubmit={handleSignIn} className="space-y-4">
          {/* Always show email field when on signin page */}
          <div>
            <label htmlFor="signin-email" className="block text-sm font-medium text-foreground">
              Email
            </label>
            <input
              id="signin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="mt-1 block w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
              disabled={isLoading}
            />
          </div>
          <div>
            <label htmlFor="signin-password" className="block text-sm font-medium text-foreground">
              Password
            </label>
            <input
              id="signin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className="mt-1 block w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
              disabled={isLoading}
            />
          </div>
          {/* Only show this when we have the email but didn't come directly to signin */}
          {isExistingUser && email && mode === 'signup' && (
            <div className="text-sm text-muted-foreground">
              <p>Signing in as: <strong>{email}</strong></p>
              <button
                type="button"
                onClick={resetForm}
                className="text-primary-600 hover:underline"
              >
                Use different email
              </button>
            </div>
          )}
          {/* Forgot Password Link */}
          <div className="text-right">
            <a href="/auth/forgot-password" className="text-sm text-primary-600 hover:underline">
              Forgot password?
            </a>
          </div>
          <button
            type="submit"
            disabled={isLoading || !password || !email}
            className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>
      )}

      {/* Email Verification Step */}
      {step === 'verify-email' && (
        <div className="text-center space-y-4">
          <div className="text-6xl">📧</div>
          <div>
            <p className="text-muted-foreground">
              We sent a verification link to <strong>{email}</strong>
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Click the link in your email to verify your account, then return here to sign in.
            </p>
          </div>
          <button
            onClick={resetForm}
            className="w-full bg-muted text-foreground py-2 px-4 rounded-md hover:bg-accent"
          >
            Use Different Email
          </button>
        </div>
      )}


      
      {/* Footer with contact sales message */}
      {(step === 'email' || step === 'password' || step === 'signin') && mode === 'signin' && (
        <div className="mt-4 text-center text-sm">
          <p className="text-muted-foreground">
            Need an account?{' '}
            <span className="text-foreground font-medium">
              Contact our sales team
            </span>
          </p>
        </div>
      )}
    </div>
  );
}